<template>
  <div class="login-component">
    <Spinner v-if="busy" />
    <div v-else class="login">
      <div v-if="currentStep === 'login'" class="content">
        <h1>Acessar conta</h1>
        <div class="register">
          Ainda não possui uma conta?
          <BaseLink
            class="link"
            @click="
              $router.push({
                path: '/criar-conta',
                query: { ...$route.query },
              })
            "
          >
            Cadastre-se
          </BaseLink>
        </div>
        <div class="content-wrapper">
          <div class="left">
            <div class="controls">
              <Button
                :class="{ active: loginMethod === 'cpf' }"
                type="outlined"
                size="small"
                @click="loginMethod = 'cpf'"
              >
                CPF
              </Button>
              <Button
                :class="{ active: loginMethod === 'email' }"
                size="small"
                type="outlined"
                @click="loginMethod = 'email'"
              >
                E-mail
              </Button>
            </div>
            <FormVee ref="observer" class="form" as="div" @submit="submit">
              <EmailInput
                v-if="loginMethod === 'email'"
                v-model="valueEmail"
                placeholder="Digite seu e-mail"
                label="Insira seu e-mail"
                :validation="validation"
                validations="required|rgx:email"
                :external-validation="userNotFound"
                :external-message="userNotFoundMessage"
                @input="onEmailChange"
              />

              <!-- Campo de CPF para login -->
              <CpfInput
                v-else-if="loginMethod === 'cpf'"
                v-model="valueCpf"
                placeholder="Insira seu CPF"
                label="Insira seu CPF"
                :validation="validation"
                :external-validation="userNotFound"
                :external-message="userNotFoundMessage"
                @input="onCpfChange"
              />
            </FormVee>
            <div class="actions">
              <Button
                v-if="loginMethod === 'email' || loginMethod === 'cpf'"
                class="continue"
                type="primary"
                :busy="loading"
                @click="submit"
              >
                Continuar
              </Button>
            </div>
          </div>
          <div class="right">
            <div class="divider-wrapper">
              <div class="divider" />
              <p>OU</p>
              <div class="divider" />
            </div>
            <div class="login-options">
              <GoogleSignInButton context="login" @login="loginAction" />
              <MicrosoftSignInButton context="login" @login="loginAction" />
            </div>
          </div>
        </div>
        <!-- text -->
      </div>
      <TokenValidationComponent
        v-else-if="currentStep === 'code'"
        :field-value="loginMethod === 'email' ? valueEmail : valueCpf"
        :user-email="userEmail"
        :user-number="userNumber"
        :login-method="loginMethod"
        @back="currentStep = 'login'"
        @login="loginAction"
      />
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { HttpStatusCode } from '@/helpers/jb-error'
import Spinner from '@/aasgard/components/feedback/Spinner.vue'
import MicrosoftSignInButton from '@/containers/oauth/providers/MicrosoftSignInButton.vue'
import GoogleSignInButton from '@/containers/oauth/providers/GoogleSignInButton.vue'
import CpfInput from '@/components/forms/fields/input/CpfInput.vue'
import EmailInput from '@/components/forms/fields/input/EmailInput.vue'
import Button from '@/aasgard/components/buttons/Button.vue'
import TokenValidationComponent from '@/components/login/TokenValidationComponent.vue'
import BaseLink from '@/aasgard/components/base/BaseLink.vue'

export default {
  name: 'LoginComponent',
  components: {
    BaseLink,
    Spinner,
    MicrosoftSignInButton,
    GoogleSignInButton,
    CpfInput,
    EmailInput,
    Button,
    TokenValidationComponent,
  },
  props: {
    busy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['login'],
  data() {
    return {
      currentStep: 'login',
      userNotFound: false,
      userNotFoundMessage: 'Usuário não cadastrado!',
      keepLogged: true,
      valueCpf: '',
      valueEmail: '',
      loginMethod: 'cpf',
      validation: false,
      showNotification: false,
      textNotification: '',
      userEmail: '',
      userNumber: '',
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      personaData: 'user/persona/personaData',
      gtUser: 'user/user/gtUser',
    }),
  },
  beforeMount() {
    const { method, value } = JSON.parse(
      sessionStorage.getItem('jb.state.send-token-data') || '{}',
    )
    if ((method === 'email' || method === 'cpf') && value) {
      this.loginMethod = method
      this.valueCpf = method === 'cpf' ? String(value ?? '') : ''
      this.valueEmail = method === 'email' ? String(value ?? '') : ''
      this.currentStep = 'code'
      this.actEmitGaEvent({
        eventCategory: `login-page`,
        eventAction: 'existent-user',
        eventLabel: 'existent-user',
      })
      sessionStorage.removeItem('jb.state.send-token-data')
    }
  },
  methods: {
    ...mapActions({
      actTokenLogin: 'token/actTokenLogin',
      actEmitGaEvent: 'services/ga/actEmitGaEvent',
      actGetUser: 'user/user/actGetUser',
      registerInsuranceTypes: 'services/tracker/registerInsuranceTypes',
    }),
    onEmailChange($event) {
      this.valueEmail = $event
      this.userNotFound = false
    },
    onCpfChange($event) {
      this.valueCpf = $event
      this.userNotFound = false
    },
    async submit() {
      this.validation = true
      const result = await this.$refs.observer?.validate()
      if (result.valid) {
        this.actEmitGaEvent({
          eventCategory: 'token-login',
          eventAction: `${this.loginMethod}-selected`,
          eventLabel: `${this.loginMethod}-selected`,
        })
        await this.submitToken({
          value: this.loginMethod === 'email' ? this.valueEmail : this.valueCpf,
          type: this.loginMethod,
          send_sms: true,
        })
      }
    },
    async submitToken({ value, type, send_sms }) {
      this.loading = true
      const response = await this.actTokenLogin({
        tokenRequest: { [type]: value, send_sms },
      })

      if (response?.status === HttpStatusCode.ACCEPTED) {
        this.userEmail = response?.data?.email
        this.userNumber = response?.data?.mobile_phone
        this.currentStep = 'code'
        this.actEmitGaEvent({
          eventCategory: `tela-de-login`,
          eventAction: 'clique-botao-continuar',
          eventLabel: 'clique-botao-continuar',
        })
      }

      this.userNotFound = response?.status === HttpStatusCode.UNAUTHORIZED

      this.loading = false
    },
    loginAction() {
      this.$emit('login')
    },
  },
}
</script>
<style lang="scss">
.login-component {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: $color-white-high;

  .not-found-message {
    @include caption;

    & > .BaseLink {
      @include caption;
    }
  }

  & > .login {
    & > .content {
      padding: 0 16px;
      margin: 0 auto;

      & > h1 {
        @include headline-5;
        color: $color-black-high;
        margin: 0 0 8px 0;
        text-align: center;

        @include media(md) {
          font-size: 32px;
          line-height: 48px;
        }
      }

      & > .register {
        text-align: center;
        @include body-1;
        color: $color-black-high;
        margin-bottom: 32px;

        & > .link {
          text-decoration: none;
        }
      }

      .content-wrapper {
        display: flex;
        flex-wrap: wrap;
        width: 300px;
        margin: 0 auto;

        @include media(md) {
          width: 768px;
        }

        .left {
          width: 300px;

          & > .controls {
            display: flex;

            .Button {
              background-color: $color-white-high;
              border: 2px solid;
              border-top: none;
              border-left: none;
              border-right: none;
              border-radius: unset;

              &.active:hover {
                position: relative;

                &:before {
                  content: unset;
                }
              }

              &:not(.active) {
                color: $color-black-medium;
                border-bottom: solid 1px $color-black-medium;

                &:hover {
                  border-bottom: solid 1px $color-brand-primary;
                }
              }
            }
          }

          & > .form {
            & > .input-field {
              margin-top: 30px;
            }

            & > .EmailInput {
              & > .email-helpers {
                margin-top: -24px;
              }
            }
          }

          & > .actions {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;

            & > .Button {
              width: 300px;
            }
          }
        }

        .right {
          display: flex;
          flex-wrap: wrap;
          width: 300px;

          @include media(md) {
            width: auto;
          }

          .divider-wrapper {
            width: 100%;
            margin: 24px 0;
            align-items: center;
            display: flex;

            @include media(md) {
              width: auto;
              flex-direction: column;
              margin: 0 64px;
            }

            .divider {
              background-color: $color-black-lower;
              width: 100%;
              height: 1px;

              @include media(md) {
                height: 100%;
                width: 1px;
              }
            }

            p {
              @include body-2;
              color: $color-black-medium;
              margin: 0 8px;

              @include media(md) {
                margin: 8px 0;
              }
            }
          }

          & > .login-options {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 16px;

            & > .separator {
              width: 100%;
              margin: 0;
              text-align: center;
              @include subtitle-2;
            }
          }
        }
      }
    }
  }
}
</style>
